"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCustomer } from "@/context/CustomerContext";
import {
  applySavedToggle,
  classifySavedFailure,
  guestMergeFeedback,
  guestMergeIds,
  guestSavedKey,
  hasAdminSessionCookie,
  readGuestSaved,
  savedFeedbackText,
  savedProductsEnabledOnPath,
  toggleGuestSaved,
  writeGuestSaved,
  type GuestSavedEntry,
  type SavedFeedback,
  type SavedProductRow,
} from "@/lib/savedProducts";
import {
  SavedProductsError,
  fetchMySavedProducts,
  saveProductsBulk,
  setProductSaved,
} from "@/lib/supabase/savedProducts";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";

const TOAST_MS = 2200;
const EMPTY_ROWS: SavedProductRow[] = [];
const NO_GUEST: GuestSavedEntry[] = [];

export const savedProductsQueryKey = (customerId: string | null) =>
  ["saved-products", customerId] as const;

type SavedProductsContextValue = {
  enabled: boolean;
  /** Akun sudah selesai dimuat — sebelum itu belum tahu tamu atau pembeli. */
  ready: boolean;
  customerId: string | null;
  guest: GuestSavedEntry[];
  setSaved: (productId: string, saved: boolean) => void;
};

const SavedProductsContext = createContext<SavedProductsContextValue>({
  enabled: false,
  ready: false,
  customerId: null,
  guest: [],
  setSaved: () => {},
});

/**
 * Simpan produk ❤ (mig 6270 · 6290) — SEKALI pasang di layout (`Providers`).
 *
 * Pembeli yang login: daftar di akun (TanStack Query `["saved-products",
 * customerId]`, ubah optimistik lalu diselaraskan ulang). Tamu: localStorage
 * per toko. Begitu tamu MASUK, simpanan perangkatnya digabung ke akun dalam
 * SATU panggilan `save_products_bulk` lalu dikosongkan — gagal = dibiarkan di
 * perangkat dan dicoba lagi pada muat halaman berikutnya. Browser yang
 * memegang sesi admin toko (impersonasi) tak pernah menggabung.
 *
 * Mati di /admin, /platform, dan /toko (`savedProductsEnabledOnPath`): tanpa
 * query, tanpa gabung, `setSaved` no-op. Selama akun masih dimuat (`ready`
 * false) `setSaved` juga no-op: pembeli yang sudah login masih terlihat
 * seperti tamu, dan simpanannya akan nyasar ke HP dengan toast "masuk agar
 * tersimpan di akunmu".
 */
export function SavedProductsProvider({ children }: { children: ReactNode }) {
  const { customer, isLoading: customerLoading } = useCustomer();
  const pathname = usePathname();
  const enabled = savedProductsEnabledOnPath(pathname);
  const ready = !customerLoading;
  const customerId = enabled ? (customer?.id ?? null) : null;
  // Toko hasil resolusi Host; NULL (apex platform) = fitur tak menyimpan apa pun.
  const storeId = getClientStoreId();
  const qc = useQueryClient();
  const [guest, setGuest] = useState<GuestSavedEntry[]>([]);
  // Browser admin toko yang membuka akun pelanggan (impersonasi): simpanan
  // perangkat milik si admin — tak digabung, tak dihitung milik akun ini.
  const [adminBrowser, setAdminBrowser] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const notify = useCallback((kind: SavedFeedback, count?: number) => {
    setToast(savedFeedbackText(kind, count));
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    []
  );

  // Simpanan perangkat; `storage` = tab lain mengubahnya.
  useEffect(() => {
    if (!enabled) return;
    setGuest(readGuestSaved(storeId));
    const onStorage = (event: StorageEvent) => {
      if (storeId && event.key === guestSavedKey(storeId)) setGuest(readGuestSaved(storeId));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [enabled, storeId]);

  // Tamu baru masuk → pindahkan simpanan perangkat ke akunnya, SATU panggilan
  // (satu transaksi → urutan perangkat terjaga; yang tak muat dikabarkan).
  const mergingRef = useRef(false);
  useEffect(() => {
    if (!customerId || mergingRef.current) return;
    const fromAdmin = hasAdminSessionCookie(document.cookie);
    setAdminBrowser(fromAdmin);
    if (fromAdmin) return;
    const pending = guestMergeIds(readGuestSaved(storeId));
    if (!pending.length) return;
    mergingRef.current = true;
    const key = savedProductsQueryKey(customerId);
    void (async () => {
      try {
        const added = await saveProductsBulk(pending);
        writeGuestSaved(storeId, []);
        setGuest([]);
        // Daftar akun SESUDAH gabung, diambil sekarang: ❤ tak berkedip kosong,
        // dan yang tak muat terbaca. Fetch daftar yang berangkat SEBELUM gabung
        // dibatalkan dulu — tanpa itu `fetchQuery` menumpang hasil basinya.
        await qc.cancelQueries({ queryKey: key });
        const rows = await qc
          .fetchQuery({ queryKey: key, queryFn: fetchMySavedProducts, staleTime: 0 })
          .catch(() => null);
        if (!rows) void qc.invalidateQueries({ queryKey: key });
        const feedback = guestMergeFeedback(
          pending,
          added,
          rows ? rows.map((row) => row.productId) : null
        );
        if (feedback) notify(feedback.kind, feedback.count);
      } catch {
        // Dibiarkan di perangkat; produk yang sudah masuk dilewati RPC saat diulang.
        void qc.invalidateQueries({ queryKey: key });
      } finally {
        mergingRef.current = false;
      }
    })();
  }, [customerId, storeId, qc, notify]);

  // RPC ❤ DISERIALKAN per produk: ketukan berikutnya pada produk yang sama baru
  // berangkat sesudah yang sebelumnya selesai, jadi niat TERAKHIR pembeli yang
  // tinggal di server. Tanpa ini "simpan" yang tertahan di jaringan bisa
  // mendarat SESUDAH "lepas" — dan rollback ke potret lama memulihkan keadaan
  // basi. Gagal = toast + selaraskan ulang dari server.
  const chainsRef = useRef(new Map<string, Promise<void>>());

  const setSaved = useCallback(
    (productId: string, saved: boolean) => {
      if (!enabled || !ready || !storeId || !productId) return;
      // Lepas juga dari simpanan perangkat yang belum sempat digabung.
      const device = toggleGuestSaved(readGuestSaved(storeId), productId, saved, Date.now());
      if (!customerId) {
        if (device.full) {
          notify("limit");
          return;
        }
        if (!device.changed) return;
        writeGuestSaved(storeId, device.list);
        setGuest(device.list);
        notify(saved ? "guest-saved" : "removed");
        return;
      }
      if (!saved && device.changed && !adminBrowser) {
        writeGuestSaved(storeId, device.list);
        setGuest(device.list);
      }

      const key = savedProductsQueryKey(customerId);
      void (async () => {
        await qc.cancelQueries({ queryKey: key });
        qc.setQueryData<SavedProductRow[]>(key, (rows) =>
          applySavedToggle(rows ?? [], productId, saved, new Date().toISOString())
        );
        notify(saved ? "saved" : "removed");
      })();

      const chains = chainsRef.current;
      const step = (chains.get(productId) ?? Promise.resolve()).then(async () => {
        try {
          await setProductSaved(productId, saved);
        } catch (error) {
          notify(classifySavedFailure(error instanceof SavedProductsError ? error.code : null));
        }
      });
      chains.set(productId, step);
      void step.then(() => {
        if (chains.get(productId) === step) chains.delete(productId);
        // Selaraskan hanya saat tak ada RPC ❤ yang masih jalan — refetch di
        // tengah jalan menimpa keadaan optimistik ketukan yang belum mendarat.
        if (!chains.size) void qc.invalidateQueries({ queryKey: key });
      });
    },
    [enabled, ready, storeId, customerId, adminBrowser, qc, notify]
  );

  // `enabled` = gerbang RENDER, sengaja hanya dari jalur: `getClientStoreId()`
  // selalu NULL di server (di-set `Providers` hanya di browser), jadi bila ikut
  // menentukan render, HTML server tanpa ❤ ≠ render klien pertama dengan ❤
  // (React #418). Toko yang tak ter-resolusi menggerbang AKSI di `setSaved`.
  // `ready` aman untuk render: `isLoading` akun bernilai awal `true` di server
  // maupun di render klien pertama.
  const accountGuest = customerId && adminBrowser ? NO_GUEST : guest;
  const value = useMemo(
    () => ({ enabled, ready, customerId, guest: accountGuest, setSaved }),
    [enabled, ready, customerId, accountGuest, setSaved]
  );

  return (
    <SavedProductsContext.Provider value={value}>
      {children}
      {toast ? (
        <div className="safe-top pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-3">
          <div
            role="status"
            className="max-w-sm rounded-2xl bg-zinc-900/95 px-4 py-2 text-center text-xs font-bold leading-snug text-white shadow-soft ring-1 ring-white/15"
          >
            {toast}
          </div>
        </div>
      ) : null}
    </SavedProductsContext.Provider>
  );
}

/**
 * Keadaan ❤ untuk komponen: `isSaved`, `toggle`, daftar akun (terbaru dulu).
 * Semua pemakai berbagi satu cache — satu RPC daftar per sesi (5 menit segar).
 * `ready` false = keadaan ❤ belum diketahui (akun dimuat / daftarnya belum
 * tiba): tombol ❤ redup-nonaktif, ketuk dua kali diabaikan.
 */
export function useSavedProducts() {
  const { enabled, ready, customerId, guest, setSaved } = useContext(SavedProductsContext);
  const query = useQuery({
    queryKey: savedProductsQueryKey(customerId),
    queryFn: fetchMySavedProducts,
    enabled: enabled && Boolean(customerId),
    staleTime: 5 * 60_000,
  });
  const rows = customerId ? (query.data ?? EMPTY_ROWS) : EMPTY_ROWS;
  // Simpanan perangkat yang belum selesai digabung tetap dihitung, supaya ❤
  // tak berkedip kosong tepat sesudah masuk.
  const savedIds = useMemo(() => {
    const ids = new Set(rows.map((row) => row.productId));
    for (const entry of guest) ids.add(entry.id);
    return ids;
  }, [rows, guest]);
  const toggle = useCallback(
    (productId: string) => setSaved(productId, !savedIds.has(productId)),
    [savedIds, setSaved]
  );
  const isLoading = enabled && Boolean(customerId) && query.isPending;

  return {
    enabled,
    ready: ready && !isLoading,
    isGuest: !customerId,
    rows,
    guest,
    savedIds,
    isSaved: (productId: string) => savedIds.has(productId),
    setSaved,
    toggle,
    isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
